import { getAccessorTightByteLength } from '@/plugins/mesh/loaders/gltfLowMemory/DefaultGltfSceneAnalyzer'
import type { GltfGeometryResourceEstimate } from '@/plugins/mesh/loaders/gltfLowMemory/preflightTypes'
import type {
  GltfBatchPlan,
  GltfManifest,
  GltfPrimitiveSource,
  GltfSceneAnalysis,
} from '@/plugins/mesh/loaders/gltfLowMemory/types'

type MutableEstimate = { -readonly [K in keyof GltfGeometryResourceEstimate]: number }

export function createEmptyGeometryEstimate(): MutableEstimate {
  return { vertexCount: 0, indexCount: 0, vertexAttributeBytes: 0, indexBytes: 0, instanceBytes: 0 }
}

export function getEstimatedGeometryBytes(geometry: GltfGeometryResourceEstimate): number {
  return geometry.vertexAttributeBytes + geometry.indexBytes + geometry.instanceBytes
}

/** 标准路径按 accessor/交错存储去重；直接路径按实际批次的紧密数组计算。 */
export function estimateGltfGeometryResources(
  manifest: GltfManifest,
  analysis: GltfSceneAnalysis,
  plan?: GltfBatchPlan
): GltfGeometryResourceEstimate {
  const result = createEmptyGeometryEstimate()
  const accessors = new Set<number>()
  const interleavedViews = new Set<string>()
  const addAccessor = (index: number, target: 'vertexAttributeBytes' | 'indexBytes') => {
    const accessor = manifest.accessors?.[index]
    if (!accessor || (!plan && accessors.has(index))) return
    accessors.add(index)
    const view = manifest.bufferViews?.[accessor.bufferView ?? -1]
    const tightBytes = getAccessorTightByteLength(accessor)
    const itemBytes = accessor.count > 0 ? tightBytes / accessor.count : 0
    if (!plan && view?.byteStride && view.byteStride !== itemBytes) {
      // 与 GLTFLoader 的 InterleavedBuffer 缓存身份保持一致：类型/切片不同会再次上传。
      const slice = Math.floor((accessor.byteOffset ?? 0) / view.byteStride)
      const key = `${accessor.bufferView}:${accessor.componentType}:${slice}:${accessor.count}`
      if (!accessor.sparse && interleavedViews.has(key)) return
      if (!accessor.sparse) interleavedViews.add(key)
      result[target] += accessor.count * view.byteStride
    } else {
      result[target] += tightBytes
    }
  }
  const positions = new Set<number>()
  const indices = new Set<number>()
  const addPrimitive = (source: GltfPrimitiveSource, includeIndex = true) => {
    const position = source.attributeAccessors.POSITION
    if (plan || !positions.has(position)) {
      result.vertexCount += source.vertexCount
      positions.add(position)
    }
    for (const index of Object.values(source.attributeAccessors))
      addAccessor(index, 'vertexAttributeBytes')
    // 保留原有兼容路径生成法线的预留，不依赖 GPU 导数是否可用。
    if (source.attributeAccessors.NORMAL === undefined)
      result.vertexAttributeBytes += source.vertexCount * 12
    if (includeIndex && source.indexAccessor !== undefined) {
      if (plan || !indices.has(source.indexAccessor)) {
        result.indexCount += source.indexCount
        indices.add(source.indexAccessor)
      }
      addAccessor(source.indexAccessor, 'indexBytes')
    }
    const primitive = manifest.meshes?.[source.meshIndex]?.primitives[source.primitiveIndex]
    for (const target of primitive?.targets ?? []) {
      for (const index of Object.values(target)) addAccessor(index, 'vertexAttributeBytes')
    }
  }
  if (plan) {
    for (const entry of plan.entries) {
      if (entry.kind === 'merged') {
        for (const source of entry.sources) addPrimitive(source, false)
        if (entry.sources[0]?.indexAccessor !== undefined) {
          const vertices = entry.sources.reduce((sum, source) => sum + source.vertexCount, 0)
          const count = entry.sources.reduce((sum, source) => sum + source.indexCount, 0)
          result.indexCount += count
          result.indexBytes += count * (vertices > 65_535 ? 4 : 2)
        }
      } else {
        addPrimitive(entry.sources[0])
        if (entry.kind === 'instanced') result.instanceBytes += entry.sources.length * 64
      }
    }
  } else {
    const primitives = new Set<string>()
    for (const source of analysis.primitives) {
      const key = `${source.meshIndex}:${source.primitiveIndex}`
      if (primitives.has(key)) continue
      primitives.add(key)
      addPrimitive(source)
    }
  }
  return result
}

/** 直接加载可能回退；逐项保留两条路径较大值，使分项和总量使用同一口径。 */
export function combineGeometryEstimates(
  source: GltfGeometryResourceEstimate,
  planned: GltfGeometryResourceEstimate
): GltfGeometryResourceEstimate {
  return {
    vertexCount: Math.max(source.vertexCount, planned.vertexCount),
    indexCount: Math.max(source.indexCount, planned.indexCount),
    vertexAttributeBytes: Math.max(source.vertexAttributeBytes, planned.vertexAttributeBytes),
    indexBytes: Math.max(source.indexBytes, planned.indexBytes),
    instanceBytes: Math.max(source.instanceBytes, planned.instanceBytes),
  }
}
